import { cn } from '@/lib/utils';
import type { Platform } from './types';

// Single source of truth for how platforms present across the consolidated
// Performance surface (#5971): display name, accent colour, and brand logo.
export const PLATFORM_LABEL: Record<Platform, string> = { google: 'Google', meta: 'Meta' };

// Google green / Meta blue. Used for chart series strokes and any platform accent
// so the two are consistently colour-coded everywhere they appear.
export const PLATFORM_COLOR: Record<Platform, string> = { google: '#34A853', meta: '#1877F2' };

// Soft badge classes (bg + text) tuned to match PLATFORM_COLOR.
const PLATFORM_BADGE_CLASS: Record<Platform, string> = {
  google: 'bg-green-50 text-green-700',
  meta: 'bg-blue-50 text-blue-700',
};

/** The four-colour Google "G" mark. */
export function GoogleLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

/** The Meta infinity mark, in Meta blue. */
export function MetaLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path
        fill={PLATFORM_COLOR.meta}
        d="M6.915 4.03c-1.968 0-3.683 1.28-4.871 3.113C.704 9.208 0 11.883 0 14.449c0 .706.07 1.369.21 1.973a6.624 6.624 0 0 0 .265.86 5.297 5.297 0 0 0 .371.761c.696 1.159 1.818 1.927 3.593 1.927 1.497 0 2.633-.671 3.965-2.444.76-1.012 1.144-1.626 2.663-4.32l.756-1.339.186-.325c.061.1.121.196.183.3l2.152 3.595c.724 1.21 1.665 2.556 2.47 3.314 1.046.987 1.992 1.22 3.06 1.22 1.075 0 1.876-.355 2.455-.843a3.743 3.743 0 0 0 .81-.973c.542-.939.861-2.127.861-3.745 0-2.72-.681-5.357-2.084-7.45-1.282-1.912-2.957-2.93-4.716-2.93-1.047 0-2.088.467-3.053 1.308-.652.57-1.257 1.29-1.82 2.05-.69-.875-1.335-1.547-1.958-2.056-1.182-.966-2.315-1.303-3.454-1.303zm10.16 2.053c1.147 0 2.188.758 2.992 1.999 1.132 1.748 1.647 4.195 1.647 6.4 0 1.548-.368 2.9-1.839 2.9-.58 0-1.027-.23-1.664-1.004-.496-.601-1.343-1.878-2.832-4.358l-.617-1.028a44.907 44.907 0 0 0-1.255-1.98c.07-.109.141-.224.211-.327 1.12-1.667 2.118-2.602 3.166-2.602zm-10.201.553c1.256 0 2.564.982 3.582 2.487-.859 1.331-1.719 2.663-2.578 3.994-1.483 2.296-2.087 2.83-3.13 2.83-.57 0-1.02-.234-1.372-.601-.541-.564-.821-1.542-.821-2.921 0-1.891.507-3.978 1.32-5.343.83-1.396 1.887-2.446 3.399-2.446z"
      />
    </svg>
  );
}

/** Just the platform's logo. */
export function PlatformLogo({ platform, className }: { platform: Platform; className?: string }) {
  return platform === 'google' ? <GoogleLogo className={className} /> : <MetaLogo className={className} />;
}

/** Logo + name inline — for toggle options, legends, and headings. */
export function PlatformName({ platform, className }: { platform: Platform; className?: string }) {
  return (
    <span className={cn('inline-flex items-center gap-1.5', className)}>
      <PlatformLogo platform={platform} className="h-3.5 w-3.5 shrink-0" />
      {PLATFORM_LABEL[platform]}
    </span>
  );
}

/** Pill badge (logo + name) for table cells. */
export function PlatformBadge({ platform }: { platform: Platform }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium',
        PLATFORM_BADGE_CLASS[platform],
      )}
    >
      <PlatformLogo platform={platform} className="h-3 w-3 shrink-0" />
      {PLATFORM_LABEL[platform]}
    </span>
  );
}
